"use client";

import { useEffect } from "react";

const DEBUG_QUERY_KEY = "d4HeaderDebug";

function isDebugEnabled() {
  if (process.env.NODE_ENV !== "development") return false;
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  return params.get(DEBUG_QUERY_KEY) === "1";
}

function round(value: number) {
  return Number(value.toFixed(2));
}

type ProbeSource = "home-v2" | "chi-sono";

export function useHeaderDebugProbe(source: ProbeSource) {
  useEffect(() => {
    if (!isDebugEnabled()) return;

    const getSnapshot = () => {
      const header = document.querySelector("header") as HTMLElement | null;
      const firstSection = document.querySelector("main > section") as
        | HTMLElement
        | null;
      const vv = window.visualViewport;

      const headerRect = header?.getBoundingClientRect();
      const sectionRect = firstSection?.getBoundingClientRect();
      const headerStyle = header ? window.getComputedStyle(header) : null;

      const snapshot = {
        source,
        path: window.location.pathname,
        scrollY: round(window.scrollY),
        innerHeight: window.innerHeight,
        clientHeight: document.documentElement.clientHeight,
        visualViewport: vv
          ? {
              width: round(vv.width),
              height: round(vv.height),
              offsetTop: round(vv.offsetTop),
              pageTop: round(vv.pageTop),
              scale: round(vv.scale),
            }
          : null,
        headerRect: headerRect
          ? {
              top: round(headerRect.top),
              bottom: round(headerRect.bottom),
              height: round(headerRect.height),
            }
          : null,
        firstSectionRect: sectionRect
          ? {
              top: round(sectionRect.top),
              bottom: round(sectionRect.bottom),
              height: round(sectionRect.height),
            }
          : null,
        headerComputed: headerStyle
          ? {
              position: headerStyle.position,
              top: headerStyle.top,
              paddingTop: headerStyle.paddingTop,
              transform: headerStyle.transform,
              backdropFilter:
                headerStyle.backdropFilter ||
                (headerStyle as unknown as Record<string, string>).webkitBackdropFilter ||
                "none",
            }
          : null,
        bodyClasses: document.body.className,
        htmlClasses: document.documentElement.className,
      };

      const isPotentiallyClipped =
        !!headerRect &&
        (headerRect.top < -1 || headerRect.height < 56 || headerRect.bottom < 40);

      return { snapshot, isPotentiallyClipped };
    };

    const log = (reason: string) => {
      const { snapshot, isPotentiallyClipped } = getSnapshot();
      const label = `[header-debug][${source}] ${reason}`;

      if (isPotentiallyClipped) {
        console.warn(label, { ...snapshot, warning: "possible-header-clipping" });
        return;
      }

      console.log(label, snapshot);
    };

    let postScrollTimer = 0;
    let lastScrollLogAt = 0;

    const handleScroll = () => {
      const now = performance.now();
      if (now - lastScrollLogAt > 180) {
        log("scroll");
        lastScrollLogAt = now;
      }

      if (postScrollTimer) {
        window.clearTimeout(postScrollTimer);
      }
      postScrollTimer = window.setTimeout(() => log("post-scroll"), 180);
    };

    const handleResize = () => log("resize");
    const handleVisibility = () => log("visibility");

    log("init");
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    document.addEventListener("visibilitychange", handleVisibility);
    window.visualViewport?.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("scroll", handleResize);

    return () => {
      if (postScrollTimer) {
        window.clearTimeout(postScrollTimer);
      }
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("scroll", handleResize);
    };
  }, [source]);
}

